import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, Zoom } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import { slugFromProductUrl } from '../data/mockCatalog';
import { useCart } from '../state/cart';
import { useWishlist } from '../state/wishlist';
import { fetchProductBySlug, fetchShopProducts } from '../api/catalog';
import '../styles/product.css';

/* ── Accordion section content ───────────────────────────────── */
function SectionDescription({ description }) {
  return (
    <div className="productPage__accordionText">
      {description.split('\n').map((para, i) => (
        <p key={i}>{para}</p>
      ))}
    </div>
  );
}

function SectionCare({ care }) {
  const items = care.split('. ').filter(Boolean).map((s) => s.replace(/\.$/, ''));
  return (
    <ul className="productPage__accordionList">
      {items.map((item, i) => (
        <li key={i}>{item}.</li>
      ))}
    </ul>
  );
}

function SectionWearing({ wearing }) {
  return (
    <div className="productPage__accordionText">
      <p>{wearing}</p>
    </div>
  );
}

function SectionShipping({ shipping }) {
  return (
    <div className="productPage__accordionText">
      <p>{shipping}</p>
      <p>
        Returns are accepted within <strong>14 days</strong>. Contact us through the{' '}
        <Link to="/contact">Contact page</Link> before sending anything back.
      </p>
    </div>
  );
}

/* ── Related product card ────────────────────────────────────── */
function RelatedCard({ product }) {
  const slug = product.slug || slugFromProductUrl(product.url);
  return (
    <Link to={`/product/${slug}`} className="productPage__relatedCard">
      <div className="productPage__relatedImgWrap">
        <img
          src={product.image || '/assets/imgs/logo-light.svg'}
          alt={product.name}
          className="productPage__relatedImg"
          loading="lazy"
        />
        {product.outOfStock && (
          <span className="productPage__relatedBadge">Sold Out</span>
        )}
      </div>
      <div className="productPage__relatedInfo">
        <p className="productPage__relatedName">{product.name}</p>
        <p className="productPage__relatedPrice">{product.priceText}</p>
      </div>
    </Link>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ProductPage
═══════════════════════════════════════════════════════════════ */
export function ProductPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [shopProducts, setShopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();

  const [qty, setQty] = useState(1);
  const [openSection, setOpenSection] = useState('description');
  const [added, setAdded] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [swiperRef, setSwiperRef] = useState(null);

  useEffect(() => {
    setProduct(null);
    setQty(1);
    setActiveIdx(0);
    setAdded(false);
    setOpenSection('description');
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const data = await fetchProductBySlug(slug, ac.signal);
        if (cancelled) return;
        setProduct(data || null);
        const { products: list } = await fetchShopProducts(ac.signal).catch(() => ({ products: [] }));
        if (!cancelled && Array.isArray(list) && list.length) {
          setShopProducts(list);
        }
      } catch (e) {
        if (cancelled || e?.name === 'AbortError') return;
        setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [slug]);

  const related = useMemo(() => {
    return shopProducts
      .filter((p) => {
        const ps = p.slug || slugFromProductUrl(p.url);
        return ps && ps !== slug && !p.outOfStock;
      })
      .slice(0, 4);
  }, [shopProducts, slug]);

  const images = product?.images?.length
    ? product.images
    : ['/assets/imgs/logo-light.svg'];
  const hasMultipleImages = images.length > 1;
  const swiperModules = useMemo(
    () =>
      hasMultipleImages ? [Autoplay, Navigation, Pagination, Zoom] : [],
    [hasMultipleImages],
  );

  const goToSlide = useCallback(
    (i) => {
      if (!swiperRef) return;
      if (hasMultipleImages && swiperRef.params?.loop) {
        swiperRef.slideToLoop(i);
      } else {
        swiperRef.slideTo(i);
      }
    },
    [swiperRef, hasMultipleImages],
  );

  const qtyCap = useMemo(() => {
    if (!product) return 20;
    const m = product.maxQty;
    if (Number.isFinite(m) && m > 0) return Math.min(999, m);
    return 20;
  }, [product]);

  useEffect(() => {
    setQty((q) => Math.min(Math.max(1, q), qtyCap));
  }, [qtyCap]);

  const wishlisted = isWishlisted(product?.id ?? '');

  if (!product && loading) {
    return (
      <main className="productPage">
        <section className="section-padding">
          <div className="container">
            <p className="opacity-8">Loading product…</p>
          </div>
        </section>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="productPage">
        <section className="section-padding">
          <div className="container">
            <div className="productPage__notFound">
              <h1>Product not found</h1>
              <p>The item you're looking for doesn't exist or may have been removed.</p>
              <Link className="butn butn-bord radius-30" to="/shop">
                <span className="text">← Back to Shop</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const outOfStock = !!product.outOfStock;
  const priceLabel =
    product.priceText ||
    (Number.isFinite(product.price) ? `$${Number(product.price).toFixed(2)}` : '');

  const handleAddToCart = () => {
    if (outOfStock) return;
    const cartId =
      product.numericId != null && Number(product.numericId) > 0
        ? `product:${product.numericId}`
        : product.slug
          ? `shop:${product.slug}`
          : product.id;
    addItem({
      id: cartId,
      slug: product.slug,
      name: product.name,
      price: product.price,
      qty,
      image: images[0],
      maxQty: qtyCap,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2200);
  };

  const toggleSection = (id) =>
    setOpenSection((cur) => (cur === id ? null : id));

  const sections = [
    {
      id: 'description',
      label: 'Description',
      content: <SectionDescription description={product.description} />,
    },
    {
      id: 'care',
      label: 'Care & Maintenance',
      content: <SectionCare care={product.care || 'Store in a cool, dry place away from moisture.'} />,
    },
    {
      id: 'wearing',
      label: 'How to Wear',
      content: <SectionWearing wearing={product.wearing || 'Suitable for dreads, locs, braids, and thick hair.'} />,
    },
    {
      id: 'shipping',
      label: 'Shipping & Returns',
      content: <SectionShipping shipping={product.shipping || 'Ships within 1–3 business days.'} />,
    },
  ];

  return (
    <main className="productPage">
      <section className="section-padding productPage__section">
        {/* ── Breadcrumb ──────────────────────────────────────── */}
        <nav className="productPage__crumb" aria-label="Breadcrumb">
          <div className="container">
            <div className="productPage__crumbInner">
              <div className="productPage__crumbLine">
                <Link className="productPage__crumbLink" to="/shop">
                  Shop
                </Link>
                <span className="productPage__crumbSep" aria-hidden="true">
                  ›
                </span>
              </div>
              <p className="productPage__crumbProduct">{product.name}</p>
            </div>
          </div>
        </nav>

        <div className="productPage__main">
          <div className="container">
            <div className="row productPage__row">
              {/* ── Gallery ───────────────────────────────────── */}
              <div className="col-lg-7 md-mb50">
                <div className="productPage__gallery">
                  <div className="productPage__sliderWrap">
                    {outOfStock && (
                      <div className="productPage__imageSoldOverlay">Sold Out</div>
                    )}

                    <Swiper
                      modules={swiperModules}
                      slidesPerView={1}
                      loop={hasMultipleImages}
                      autoplay={
                        hasMultipleImages
                          ? {
                              delay: 4200,
                              disableOnInteraction: false,
                              pauseOnMouseEnter: true,
                            }
                          : false
                      }
                      navigation={
                        hasMultipleImages
                          ? {
                              prevEl: '.ppSlider__prev',
                              nextEl: '.ppSlider__next',
                            }
                          : false
                      }
                      pagination={
                        hasMultipleImages
                          ? { clickable: true, el: '.ppSlider__dots' }
                          : false
                      }
                      zoom={hasMultipleImages ? { maxRatio: 2.5 } : undefined}
                      onSwiper={setSwiperRef}
                      onSlideChange={(sw) => setActiveIdx(sw.realIndex)}
                      className="ppSlider__swiper"
                    >
                      {images.map((src, i) => (
                        <SwiperSlide key={i}>
                          <div className="swiper-zoom-container ppSlider__zoom">
                            <img
                              src={src}
                              alt={`${product.name} — view ${i + 1}`}
                              className="ppSlider__img"
                              loading={i === 0 ? 'eager' : 'lazy'}
                              draggable={false}
                            />
                          </div>
                        </SwiperSlide>
                      ))}
                    </Swiper>

                    {hasMultipleImages && (
                      <>
                        <button
                          type="button"
                          className="ppSlider__nav ppSlider__prev"
                          aria-label="Previous image"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="ppSlider__nav ppSlider__next"
                          aria-label="Next image"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                        <div className="ppSlider__dots" />
                      </>
                    )}
                  </div>

                  {hasMultipleImages && (
                    <div className="productPage__thumbs">
                      {images.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`productPage__thumb${activeIdx === i ? ' productPage__thumb--active' : ''}`}
                          onClick={() => goToSlide(i)}
                          aria-label={`View image ${i + 1}`}
                        >
                          <img src={src} alt="" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Product info ─────────────────────────────── */}
              <div className="col-lg-5">
                <div className="productPage__info">
                  <div className="productPage__infoHead">
                    {outOfStock && <span className="productPage__soldBadge">Sold Out</span>}
                    {!outOfStock && product.badge && (
                      <span className="productPage__badge">{product.badge}</span>
                    )}

                    <h1 className="productPage__title">{product.name}</h1>

                    <div className="productPage__priceRow">
                      <div className="productPage__price">{priceLabel}</div>
                      {!outOfStock && (
                        <div className="productPage__stock">
                          <span className="productPage__stockDot" />
                          In stock
                        </div>
                      )}
                    </div>

                    <div className="productPage__wishRow">
                      <button
                        type="button"
                        className={`productPage__wishBtn${wishlisted ? ' productPage__wishBtn--on' : ''}`}
                        aria-pressed={wishlisted}
                        aria-label={wishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                        onClick={() =>
                          toggleWishlist({
                            id: product.id,
                            slug: product.slug,
                            name: product.name,
                            price: product.price,
                            priceText: product.priceText || null,
                            image: images[0],
                            outOfStock,
                          })
                        }
                      >
                        <i
                          className={wishlisted ? 'fas fa-heart' : 'far fa-heart'}
                          aria-hidden="true"
                        />
                        <span>{wishlisted ? 'Saved to wishlist' : 'Add to wishlist'}</span>
                      </button>
                    </div>
                  </div>

                  <p className="productPage__shortDesc">
                    Handcrafted copper hair jewelry made by Elena RD. Each piece is unique — no two
                    are identical in colour or detail.
                  </p>

                  <div className="productPage__divider" />

                  {!outOfStock && (
                    <div className="productPage__qtyRow">
                      <span className="productPage__qtyLabel">Qty</span>
                      <div className="productPage__qtyControl">
                        <button
                          type="button"
                          className="productPage__qtyBtn"
                          onClick={() => setQty((q) => Math.max(1, q - 1))}
                          aria-label="Decrease quantity"
                        >
                          −
                        </button>
                        <span className="productPage__qtyVal">{qty}</span>
                        <button
                          type="button"
                          className="productPage__qtyBtn"
                          onClick={() => setQty((q) => Math.min(qtyCap, q + 1))}
                          aria-label="Increase quantity"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="productPage__actions">
                    <button
                      type="button"
                      className={`productPage__addBtn${added ? ' productPage__addBtn--added' : ''}`}
                      disabled={outOfStock}
                      onClick={handleAddToCart}
                    >
                      {added ? '✓ Added to Cart' : outOfStock ? 'Sold Out' : 'Add to Cart'}
                    </button>
                    <Link className="productPage__cartLink" to="/cart">
                      View Cart
                    </Link>
                  </div>

                  <div className="productPage__trust">
                    <div className="productPage__trustItem">
                      <span className="productPage__trustIcon">✈</span>
                      <span>Free worldwide shipping via FedEx Express</span>
                    </div>
                    <div className="productPage__trustItem">
                      <span className="productPage__trustIcon">↩</span>
                      <span>14-day returns accepted</span>
                    </div>
                    <div className="productPage__trustItem">
                      <span className="productPage__trustIcon">✦</span>
                      <span>Handmade in Malaysia — ~70 hrs per piece</span>
                    </div>
                    <div className="productPage__trustItem">
                      <span className="productPage__trustIcon">💳</span>
                      <span>Pay via PayPal, Mastercard or Visa</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* ── Accordion sections ──────────────────────────── */}
          <div className="productPage__accordion">
            {sections.map((sec) => {
              const isOpen = openSection === sec.id;
              return (
                <div
                  key={sec.id}
                  className={`productPage__accordionItem${isOpen ? ' productPage__accordionItem--open' : ''}`}
                >
                  <button
                    type="button"
                    className="productPage__accordionBtn"
                    onClick={() => toggleSection(sec.id)}
                    aria-expanded={isOpen}
                  >
                    <span>{sec.label}</span>
                    <span className="productPage__accordionChevron">+</span>
                  </button>
                  {isOpen && (
                    <div className="productPage__accordionBody">
                      {sec.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Related products ────────────────────────────── */}
            {related.length > 0 && (
              <div className="productPage__related">
                <h2 className="productPage__relatedTitle">You May Also Like</h2>
                <div className="productPage__relatedGrid">
                  {related.map((p) => (
                    <RelatedCard
                      key={p.numericId != null ? `r-${p.numericId}` : p.url}
                      product={p}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
